import React, { useState, useCallback, useEffect, memo } from 'react';
import { Box, TextField, IconButton, CircularProgress, Tooltip, Typography, Button } from '@mui/material';
import { Send } from 'lucide-react';

// Extracted ChatInput component with local state management
const ChatInput = memo(({
  handleSubmit,
  isLoading,
  darkMode,
  fileUploadProgress,
  quickActions = [],
  showQuickActions = true
}) => {
  const [message, setMessage] = useState(''); // Local state for input
  const [messageSent, setMessageSent] = useState(false);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    // Send message on Ctrl+Enter or Command+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      if (message.trim()) {
        handleSubmitMessage();
      }
      e.preventDefault(); // Prevent default newline behavior
    }
  }, [message]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    setMessage(e.target.value);
  }, []);

  // Handle form submission
  const handleSubmitMessage = useCallback(() => {
    if (isLoading || !message.trim()) return;
    
    handleSubmit(message);
    setMessage('');
    setMessageSent(true);
  }, [message, isLoading, handleSubmit]);

  const onSubmitMessage = useCallback((e) => {
    e.preventDefault();
    handleSubmitMessage();
  }, [handleSubmitMessage]);

  const handleQuickAction = useCallback((action) => {
    if (!action?.prompt || isLoading) return;

    const mode = action.mode || 'send';
    if (mode === 'fill') {
      setMessage(action.prompt);
      return;
    }

    handleSubmit(action.prompt);
    setMessage('');
    setMessageSent(true);
  }, [handleSubmit, isLoading]);

  // Reset messageSent flag for animation
  useEffect(() => {
    if (messageSent) {
      const timer = setTimeout(() => {
        setMessageSent(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [messageSent]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {showQuickActions && quickActions.length > 0 && (
        <Box
          sx={{
            px: 2,
            pt: 1,
            pb: 1,
            borderTop: '1px solid',
            borderColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
            bgcolor: darkMode ? 'rgba(255,255,255,0.015)' : 'rgba(148,163,184,0.06)',
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, letterSpacing: '0.01em' }}>
            Percorso consigliato
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
            {quickActions.map((action) => (
              <Tooltip key={action.id} title={action.description || ''} arrow>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isLoading}
                    onClick={() => handleQuickAction(action)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '999px',
                      px: 1.1,
                      py: 0.25,
                      fontSize: '0.7rem',
                      borderColor: darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(37,99,235,0.35)',
                      color: darkMode ? 'rgba(255,255,255,0.9)' : 'rgba(15,23,42,0.9)',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: darkMode ? 'rgba(59,130,246,0.18)' : 'rgba(37,99,235,0.08)',
                      },
                    }}
                  >
                    {action.label}
                  </Button>
                </span>
              </Tooltip>
            ))}
          </Box>
          <Typography variant="caption" sx={{ mt: 0.65, display: 'block', opacity: 0.65 }}>
            Seleziona uno step o scrivi direttamente una richiesta.
          </Typography>
        </Box>
      )}

      {/* File upload progress indicator */}
      {fileUploadProgress && (
        <Box sx={{ 
          p: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
          borderTop: '1px solid',
          borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography variant="caption">
            Uploading {fileUploadProgress.filename}...
            {fileUploadProgress.status === 'error' && ` Error: ${fileUploadProgress.error}`}
          </Typography>
        </Box>
      )}

      <Box
        component="form"
        onSubmit={onSubmitMessage}
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          display: 'flex',
          bgcolor: darkMode ? '#1E1E1E' : '#FFFFFF',
          position: 'relative',
          alignItems: 'flex-end',
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={isLoading ? "L'assistente sta scrivendo..." : "Scrivi una richiesta oppure usa il workflow rapido qui sopra..."}
          disabled={isLoading}
          InputProps={{
            endAdornment: null,
            sx: {
              borderRadius: '24px',
              p: '8px 16px',
              bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              '&:hover': {
                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'
              },
              '&.Mui-focused': {
                boxShadow: darkMode ? '0 0 0 2px rgba(144, 202, 249, 0.5)' : '0 0 0 2px rgba(25, 118, 210, 0.25)'
              }
            }
          }}
          variant="standard"
          sx={{
            '& .MuiInputBase-root': {
              p: 0,
              borderBottom: 'none',
            },
            mr: 1,
          }}
        />
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Send button */}
          <IconButton
            type="submit"
            disabled={!message.trim() || isLoading}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              },
              width: '48px',
              height: '48px',
              transition: 'all 0.2s',
              transform: messageSent ? 'translateY(-5px)' : 'none',
            }}
          >
            <Send size={20} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
});

export default ChatInput; 
